import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStore, useUser, callAPI, usePage } from "../../../Utils/utils";
import { category_page_labels } from "../../../Constants/Labels";
import {
  categories_page_message,
  select_store_message,
} from "../../../Constants/Messages";
import "./CategoryList.css";

export default function CategoryList({ mode, searchResult, loader }) {
  const navigate = useNavigate();
  const { store } = useStore();
  const storeId = store?.id;
  const { user } = useUser();
  const { setPage } = usePage();
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [storeData, setStoreData] = useState(null);

  // Fetch store data to get category_order
  useEffect(() => {
    if (storeId) {
      callAPI(
        `/admin/stores/${storeId}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            token: user?.auth_token,
          },
        },
        navigate,
        (loading) => setPage({ isLoading: loading }),
      ).then((data) => {
        if (data.success && data.record) {
          setStoreData(data.record);
        }
      });
    }
  }, [storeId]);

  // Initialize and sort categoryOrder when searchResult or storeData changes
  useEffect(() => {
    if (searchResult && searchResult.length > 0) {
      let allCategories = [...searchResult];

      // Sort by store's category_order if available
      const categoryOrderMap = new Map(
        (storeData?.category_order || []).map((item) => [
          item.category_id,
          item.sort_order,
        ]),
      );

      // Separate parents and children
      const parents = allCategories.filter((cat) => !cat.parent_category_id);
      const children = allCategories.filter((cat) => cat.parent_category_id);

      // Sort parents
      parents.sort((a, b) => {
        const orderA = categoryOrderMap.get(a.category_id) || 999;
        const orderB = categoryOrderMap.get(b.category_id) || 999;
        return orderA - orderB;
      });

      // Group children under parents and sort them
      let finalOrderedCategories = [];
      parents.forEach((parent) => {
        finalOrderedCategories.push(parent);
        const parentChildren = children.filter(
          (child) => child.parent_category_id == parent.category_id,
        );
        parentChildren.sort((a, b) => {
          const orderA = categoryOrderMap.get(a.category_id) || 999;
          const orderB = categoryOrderMap.get(b.category_id) || 999;
          return orderA - orderB;
        });
        finalOrderedCategories.push(...parentChildren);
      });

      // Add any categories that might have been missed (e.g., orphans)
      const addedIds = new Set(
        finalOrderedCategories.map((c) => c.category_id),
      );
      const orphans = allCategories.filter(
        (cat) => !addedIds.has(cat.category_id),
      );
      finalOrderedCategories.push(...orphans);

      setCategoryOrder(finalOrderedCategories);
    }
  }, [searchResult, storeData]);

  // onclick to navigate categoryedit page based on id
  const editProduct = (id) => {
    // if (store?.is_separate_store) {
      navigate(`/admin/category/edit/${id}`);
    // }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    const category = categoryOrder[index];
    setDraggedItem(category);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      const draggedCategory = categoryOrder[draggedIndex];
      const dropCategory = categoryOrder[dropIndex];

      // CASE 1: Dragging a Parent Category
      if (!draggedCategory.parent_category_id) {
        // Determine the group of items to move (Parent + all its children)
        let groupToMove = [draggedCategory];
        let i = draggedIndex + 1;
        while (
          i < categoryOrder.length &&
          categoryOrder[i].parent_category_id == draggedCategory.category_id
        ) {
          groupToMove.push(categoryOrder[i]);
          i++;
        }

        const newOrderWithoutGroup = categoryOrder.filter(
          (cat) =>
            !groupToMove.some((item) => item.category_id === cat.category_id),
        );

        // Find where to drop relative to the new list
        // If dropping on a child, we actually want to drop relative to its parent group
        let actualDropIndex = newOrderWithoutGroup.findIndex(
          (cat) =>
            cat.category_id ===
            (dropCategory.parent_category_id
              ? dropCategory.parent_category_id
              : dropCategory.category_id),
        );

        // If dragging down, we might want to drop after the last child of the target parent
        if (draggedIndex < dropIndex) {
          actualDropIndex++;
          // Find target parent's children count
          const targetParentId =
            dropCategory.parent_category_id || dropCategory.category_id;
          let j = actualDropIndex;
          while (
            j < newOrderWithoutGroup.length &&
            newOrderWithoutGroup[j].parent_category_id == targetParentId
          ) {
            actualDropIndex++;
            j++;
          }
        }

        const newOrder = [...newOrderWithoutGroup];
        newOrder.splice(actualDropIndex, 0, ...groupToMove);

        setCategoryOrder(newOrder);
        persistOrder(newOrder);
      }
      // CASE 2: Dragging a Child Category
      else {
        // Restrict drop to the same parent's children
        if (
          draggedCategory.parent_category_id ===
            dropCategory.parent_category_id ||
          draggedCategory.parent_category_id === dropCategory.category_id
        ) {
          const newOrder = [...categoryOrder];
          const [removed] = newOrder.splice(draggedIndex, 1);
          newOrder.splice(dropIndex, 0, removed);

          setCategoryOrder(newOrder);
          persistOrder(newOrder);
        }
      }
    }
    setDraggedIndex(null);
  };

  const persistOrder = (newOrder) => {
    // Map to the requested format: [{sort_order: index + 1, category_id: Cat.category_id}, ...]
    const formattedOrder = newOrder.map((cat, index) => ({
      sort_order: index + 1,
      category_id: cat.category_id,
    }));

    const payload = {
      store_id: storeId,
      category_order: formattedOrder,
    };

    // Call API to persist the new order
    callAPI(
      "/admin/stores/update",
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          token: user?.auth_token,
        },
        body: JSON.stringify(payload),
      },
      navigate,
      (loading) => setPage({ isLoading: loading }),
    );
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <>
      <div className={loader ? "loderHide" : "loderShow"}>
        {!storeId || storeId === "Admin" ? (
          <p className="text-center">{select_store_message?.select_store}</p>
        ) : (
          <div>
            {!searchResult || searchResult.length === 0 ? (
              <div className="card">
                <div className="card-body defaultText">
                  <p className="text-center">{categories_page_message[mode]}</p>
                </div>
              </div>
            ) : (
              <div className="tableOutline">
                <table className="table">
                  <thead>
                    <tr>
                      {mode === "active" && <th className="col-lg-1"></th>}
                      <th
                        className={mode === "active" ? "col-lg-5" : "col-lg-6"}
                      >
                        {
                          category_page_labels?.category_page_table_head_labels
                            ?.title
                        }
                      </th>
                      <th
                        className={mode === "active" ? "col-lg-4" : "col-lg-5"}
                      >
                        {
                          category_page_labels?.category_page_table_head_labels
                            ?.status
                        }
                      </th>

                      <th className="col-lg-1">
                        {
                          category_page_labels?.category_page_table_head_labels
                            ?.action
                        }
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {categoryOrder?.map((Category, index) => {
                      const status =
                        Category.status === "active" ? "Active" : "Inactive";
                      const isChild = !!Category.parent_category_id;
                      return (
                        <tr
                          key={Category.category_id}
                          draggable={mode === "active"}
                          onDragStart={
                            mode === "active"
                              ? (e) => handleDragStart(e, index)
                              : undefined
                          }
                          onDragOver={
                            mode === "active" ? handleDragOver : undefined
                          }
                          onDrop={
                            mode === "active"
                              ? (e) => handleDrop(e, index)
                              : undefined
                          }
                          onDragEnd={
                            mode === "active" ? handleDragEnd : undefined
                          }
                          className={`${draggedIndex === index ? "dragging" : ""} ${isChild ? "childRow" : "parentRow"}`}
                          style={{
                            cursor: mode === "active" ? "move" : "default",
                          }}
                        >
                          {mode === "active" && (
                            <td className="drag-handle">
                              <span className="mdi mdi-drag-vertical drag-icon"></span>
                            </td>
                          )}
                          <td
                            onClick={() => editProduct(Category.category_id)}
                            className="nameCol"
                          >
                            {isChild && (
                              <span className="childIndicator"> — </span>
                            )}
                            {Category.name}
                          </td>
                          <td>{status}</td>
                            <td>
                              <span
                                className="mdi mdi-account-edit icon"
                                onClick={() =>
                                  editProduct(Category.category_id)
                                }
                              ></span>
                            </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
